import { useIsCalendarEventTitleEnabled } from '@/features/flags';
import { useMutateStatePostIntegrationWorkflowSession } from '@/hooks/mutation/integration/direct-backend/useMutatePostIntegrationWorkflowSession';
import { cn } from '@/lib/cn';
import { parseTimeStamp } from '@/lib/parseTimeStamp';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { type ScheduleItem } from '@heidi/integrations/ehr/contracts';
import { startTracking, TrackingKey } from '@heidi/runtime/performance';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { getTimezone } from '@heidi/utils/get-country';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { memo, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

type Props = {
  appointment: ScheduleItem['appointment'];
  patient: ScheduleItem['patient'];
};

/**
 * `provider_details` for calendar-style integrations (e.g. Google Calendar)
 * carries the event `summary`/`event_id` at runtime, but the generated
 * OpenAPI type only declares `provider` + `unique_identifier`. Read the
 * extra fields through this local widening rather than editing the
 * generated schema.
 */
type ScheduleProviderDetails = ScheduleItem['appointment']['provider_details'] & {
  summary?: string | null;
  event_id?: string | null;
};

export const EhrScheduleItem = memo(({ appointment, patient }: Props) => {
  const router = useRouter();
  const intl = useIntl();
  const sessionIdFromRoute = useCurrentSessionId();
  const isCalendarEventTitleEnabled = useIsCalendarEventTitleEnabled();
  const patientName = patient?.name?.at(0);

  const displayName = useMemo(() => {
    if (isCalendarEventTitleEnabled) {
      const summary = (
        appointment.provider_details as ScheduleProviderDetails
      ).summary?.trim();
      if (summary) return summary;
    }

    const readablePatientName = [patientName?.given?.join(' '), patientName?.family]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (readablePatientName) return readablePatientName;

    return intl.formatMessage({
      defaultMessage: 'Appointment',
      id: '82aMWanqxX',
    });
  }, [appointment.provider_details, patientName, intl, isCalendarEventTitleEnabled]);

  const apptDateTimeString = format(
    parseTimeStamp(appointment.start, false),
    'yyyy-MM-dd h:mma'
  );

  const sessionIdFromAppt = appointment.session?.session_id;

  const isActiveSession = sessionIdFromAppt === sessionIdFromRoute;

  const [isNavigating, setIsNavigating] = useState(false);

  const { mutateAsync: postIntegrationWorkflowSession, isPending } =
    useMutateStatePostIntegrationWorkflowSession();

  const handleClick = useCallback(async () => {
    if (isPending || isNavigating || isActiveSession) return;

    try {
      if (sessionIdFromAppt) {
        setIsNavigating(true);
        startTracking({
          key: TrackingKey.SCRIBE_SESSION_OPEN,
          metadata: { session_id: sessionIdFromAppt },
        });
        await router.push(`/scribe/session/${sessionIdFromAppt}`);
      } else {
        setIsNavigating(true);
        await postIntegrationWorkflowSession({
          session: {
            // Validate appointment id existence for other EHRs
            is_auto_mode: false,
            ehr_appointment_id: appointment.provider_details.unique_identifier.toString(),
            timezone: getTimezone(),
          }, // only for backward compatibility
          appointment: appointment,
          patient: patient,
        });
      }
    } catch (error) {
      console.error('Error handling schedule item click:', error);
    } finally {
      setIsNavigating(false);
    }
  }, [
    appointment,
    patient,
    postIntegrationWorkflowSession,
    router,
    sessionIdFromAppt,
    isPending,
    isNavigating,
    isActiveSession,
  ]);

  const isLoading = isPending || isNavigating;

  return (
    <Button
      size="lg"
      variant="ghost"
      type="button"
      disabled={isLoading}
      className={cn(
        'group flex h-12 w-full cursor-pointer justify-start gap-x-3 hover:bg-background-secondary',
        isActiveSession && 'bg-background-secondary',
        isLoading && 'cursor-not-allowed opacity-50'
      )}
      onClick={() => void handleClick()}
    >
      <div className="ml-2 flex flex-col overflow-hidden">
        <TypographyP2 className="truncate duration-200">{displayName}</TypographyP2>
        <TypographyCaption>{apptDateTimeString}</TypographyCaption>
      </div>
    </Button>
  );
});

EhrScheduleItem.displayName = 'EhrScheduleItem';
export default EhrScheduleItem;
